import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./context/CartContext";
import { apiUrl } from "../config/config";
import image from "../assets/33_360x.webp";
import SEO from "./SEO";
import CartSkeleton from "./CartSkeleton";
// -----------------------------------------------------
// SAFE: getUserOrGuestId SHOULD NOT USE ANY HOOK
// -----------------------------------------------------
const getUserOrGuestId = () => {
  const user = localStorage.getItem("user");

  // Logged-in user
  if (user) {
    try {
      const parsed = JSON.parse(user);
      if (parsed?.id) {
        return { user_id: parsed.id, guest_id: null };
      }
    } catch {}
  }

  // Guest user
  let guest_id = localStorage.getItem("guest_id");
  if (!guest_id) {
    guest_id = "guest_" + Math.random().toString(36).substring(2, 12);
    localStorage.setItem("guest_id", guest_id);
  }

  return { user_id: null, guest_id };
};

export default function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { refreshCart } = useCart(); // ✅ SAFE — INSIDE COMPONENT ONLY!

  // -----------------------------------------------------
  // Fetch Cart Items
  // -----------------------------------------------------
  useEffect(() => {
    const { user_id, guest_id } = getUserOrGuestId();

    let url = user_id
      ? `${apiUrl}/api/cart/user/${user_id}`
      : `${apiUrl}/api/cart?guest_id=${guest_id}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCartItems(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Cart fetch error:", err);
        setLoading(false);
      });
  }, []);

  // -----------------------------------------------------
  // Delete Item
  // -----------------------------------------------------
const deleteItem = async (id) => {
  const { user_id, guest_id } = getUserOrGuestId();

  await fetch(`${apiUrl}/api/cart/${id}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id,
      guest_id
    })
  });

  // Remove from UI immediately
  setCartItems(prev => prev.filter(item => item.id !== id));

  // 🚀 NOW refresh will fetch correct cart
  refreshCart();
};


  // -----------------------------------------------------
  // Total Price
  // -----------------------------------------------------
  const total = cartItems.reduce(
    (sum, item) => sum + Number(item.price) ,
    0
  );

if (loading) return <CartSkeleton />;

  // -----------------------------------------------------
  // EMPTY CART VIEW
  // -----------------------------------------------------
  if (cartItems.length === 0) {
    return (
      <div className="max-w-3xl mx-auto text-center py-20">
        <h1 className="text-4xl font-bold mb-4">Your Cart is Empty</h1>
        <p className="text-gray-600 mb-6">
          Looks like you haven’t added anything yet.
        </p>

        <a
          href="/products"
          className="px-6 py-3 bg-black text-white rounded-lg text-lg font-semibold"
        >
          Continue Shopping
        </a>
      </div>
    );
  }

  // -----------------------------------------------------
  // MAIN CART LAYOUT
  // -----------------------------------------------------
  return (
    <>
          <SEO
                    title='Cart'
                    description='Personalize Patches For Hats'
                    keywords='Personalize Patches For Hats'
                  />
    <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
      
      {/* LEFT SIDE */}
      <div className="lg:col-span-2">
        <h1 className="text-4xl font-bold mb-4">Shopping Cart</h1>

     <div className="border rounded-xl overflow-hidden">

  {/* HEADER (hide on mobile) */}
  <div className="hidden md:grid grid-cols-12 bg-gray-50 px-6 py-4 font-semibold text-gray-700 text-sm border-b">
    <div className="col-span-7">Product</div>
    <div className="col-span-3 text-center">Quantity</div>
    <div className="col-span-2 text-right">Total</div>
  </div>

  {/* FREE PROOF */}
  <div className="flex flex-col md:grid md:grid-cols-12 px-4 md:px-6 py-6 border-b gap-4">
    
    <div className="md:col-span-7 flex gap-4">
      <img src={image} className="w-16 h-16 md:w-20 md:h-20" alt="" />
      <h3 className="font-semibold text-base md:text-lg">
        Free Proof Approval Before Production
      </h3>
    </div>

    <div className="flex justify-between md:justify-center md:col-span-3 font-semibold">
      <span className="md:hidden">Qty:</span> 1
    </div>

    <div className="flex justify-between md:justify-end md:col-span-2 font-semibold">
      <span className="md:hidden">Total:</span> $0.00
    </div>
  </div>

  {/* CART ITEMS */}
  {cartItems.map((item) => (
    <div
      key={item.id}
      className="flex flex-col md:grid md:grid-cols-12 gap-4 px-4 md:px-6 py-6 border-b"
    >

      {/* PRODUCT */}
      <div className="md:col-span-7 flex gap-4">
        <img
          src={
            item.upload_image
              ? `${apiUrl}/storage/${item.upload_image}`
              : "/img/no-image.png"
          }
          className="w-20 h-20 md:w-24 md:h-24 rounded-md border object-cover"
          alt={item.product_name}
        />

        <div>
          <h3 className="font-bold text-base md:text-lg">
            {item.product_name}
          </h3>

          <div className="text-sm text-gray-700 mt-2 space-y-1">

            {item?.width && item?.height && (
              <>
                <p><strong>Width:</strong> {item.width}"</p>
                <p><strong>Height:</strong> {item.height}"</p>
              </>
            )}

            {item.selected_filters &&
              Object.entries(item.selected_filters).map(([lbl, val]) => (
                <p key={lbl}>
                  <strong>{lbl}:</strong> {val}
                </p>
              ))}

            {item.inputs?.border_thread_color && (
              <p><strong>Thread Color:</strong> {item.inputs.border_thread_color}</p>
            )}

            {item.inputs?.special_notes && (
              <p><strong>Notes:</strong> {item.inputs.special_notes}</p>
            )}
          </div>
        </div>
      </div>

      {/* QTY + DELETE */}
      <div className="md:col-span-3 flex justify-between md:justify-center items-center">
        
        <span className="md:hidden font-medium">Qty:</span>

        <div className="flex items-center gap-3">
          <button
            onClick={() => deleteItem(item.id)}
            className="p-2 border rounded-md hover:bg-gray-100"
          >
            🗑
          </button>

          <div className="flex border rounded-md px-4 py-2 font-semibold">
            {item.qty}
          </div>
        </div>
      </div>

      {/* TOTAL */}
      <div className="md:col-span-2 flex justify-between md:justify-end items-center">
        <span className="md:hidden font-medium">Total:</span>

        <div className="text-lg font-semibold">
          ${item.price}
        </div>
      </div>

    </div>
  ))}
</div>
        
      </div>

      {/* RIGHT SIDE SUMMARY */}
      <div className="lg:col-span-1 space-y-6">
        <div className="border p-5 rounded-xl">
          <p className="font-semibold">Total</p>
          <p className="text-3xl font-bold mt-2">${total.toFixed(2)}</p>

          <p className="text-xs text-gray-500 mt-3">
            Taxes and shipping calculated at checkout.
          </p>

          <button
            onClick={() => navigate("/checkout")}
            disabled={cartItems.length === 0}
            className={`w-full py-3 mt-4 rounded-lg text-lg font-semibold
              ${
                cartItems.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-black text-white hover:bg-gray-900"
              }`}
          >
            Checkout
          </button>
        </div>
      </div>

    </div>
    </>
  );
}
